import { RowSelectionState } from '@tanstack/react-table';
import { Button, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { useCanCommitDraftPlots } from '@/hooks/useCanCommitDraftPlots';

import { BulkEditDropdown } from './BulkEditDropdown';

export const FOOTER_CLASS = 'flex shrink-0 items-center justify-between border-t border-neutral-black-12 px-6 py-4';

type DraftPlotsReviewFooterProps = {
  rowSelection: RowSelectionState;
  organizationId: string;
  canCommit: boolean;
  cancelLabel: string;
  onCancel: () => void;
  onSaveDraftPlots: () => void;
  onCommit: () => void;
  onRefetch: () => void;
  onAddToGroup: () => void;
};

/**
 * Shared footer for the review state used by both UploadPlots and UploadCoordinates.
 * - Left side shows a "N selected" badge when rows are selected, or the cancel button otherwise.
 * - BulkEditDropdown is only shown when rows are selected.
 * - Save Draft and Submit are always visible.
 */
export const DraftPlotsReviewFooter = ({
  rowSelection,
  organizationId,
  canCommit,
  cancelLabel,
  onCancel,
  onSaveDraftPlots,
  onCommit,
  onRefetch,
  onAddToGroup,
}: DraftPlotsReviewFooterProps) => {
  const { t } = useTranslation();
  const canCommitDraftPlots = useCanCommitDraftPlots();
  const selectedIds = Object.entries(rowSelection)
    .filter(([, v]) => v)
    .map(([k]) => k);
  const selectedCount = selectedIds.length;

  return (
    <div className={FOOTER_CLASS}>
      {selectedCount > 0 ? (
        <span className='typography-body2 text-text-secondary'>
          {t('shared.uploadPlots.review.selectedCount', { count: selectedCount })}
        </span>
      ) : (
        <Button variant='outline' onClick={onCancel}>
          {cancelLabel}
        </Button>
      )}

      <Stack direction='row' className='gap-3'>
        {selectedCount > 0 && (
          <BulkEditDropdown
            selectedIds={selectedIds}
            organizationId={organizationId}
            onSuccess={onRefetch}
            onAddToGroup={onAddToGroup}
          />
        )}
        <Button variant='outline' onClick={onSaveDraftPlots}>
          {t('global.ui.buttons.saveDraftPlots')}
        </Button>
        {canCommitDraftPlots && (
          <Button onClick={onCommit} disabled={!canCommit}>
            {selectedCount > 0
              ? t('shared.uploadPlots.review.submit', { count: selectedCount })
              : t('global.ui.buttons.finish')}
          </Button>
        )}
      </Stack>
    </div>
  );
};
